import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bar, CartesianGrid, ComposedChart, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  ESTADOS_OT_ABIERTOS,
  ETIQUETA_ESTADO_OT,
  ETIQUETA_ESTADO_SOLICITUD,
  ETIQUETA_PRIORIDAD,
  formatearFechaHora,
  type EstadoOT,
  type EstadoSolicitud,
  type Pagina,
  type Prioridad,
} from '@cmms/shared';
import { api, consulta } from '../servicios/api.js';
import { useSesion } from '../servicios/sesion.js';
import { useEsMovil } from '../servicios/responsive.js';
import {
  Aviso,
  Cargando,
  EstadoSolicitudEtiqueta,
  Indicador,
  PrioridadEtiqueta,
  MiniaturaImagenTabla,
  Paginacion,
  Vacio,
  mensajeError,
  useCierreDialogo,
} from '../componentes/piezas.js';
import { IconoEngranaje, IconoMas, IconoSolicitud } from '../componentes/iconos.js';
import { EnlaceRuta } from '../componentes/enlace-ruta.js';
import { useTiposActivo } from '../componentes/selectores.js';

/** Primer día del mes actual en Lima (AAAA-MM-DD). */
function primerDiaDelMesLima(): string {
  const hoy = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Lima' }).format(new Date());
  return `${hoy.slice(0, 8)}01`;
}

interface Solicitud {
  id: string;
  numero: number;
  titulo: string;
  estado: EstadoSolicitud;
  ubicacion: string | null;
  ubicacionId: string | null;
  prioridad: Prioridad;
  solicitadoEl: string;
}

interface OrdenTrabajo {
  id: string;
  numero: number;
  titulo: string;
  estado: EstadoOT;
  prioridad: Prioridad;
  ubicacion: string | null;
  ubicacionId: string | null;
  fechaVencimiento: string | null;
  vencida: boolean;
  asignados: { id: string; nombre: string; tipo: 'USUARIO' | 'GRUPO' }[];
}

interface ResumenOrdenes {
  porEstado: { estado: EstadoOT; cantidad: number }[];
  vencidas: number;
  vencidasPorPrioridad: { prioridad: Prioridad; cantidad: number }[];
}

export function Dashboard(): JSX.Element {
  const { usuario } = useSesion();
  if (!usuario) return <Cargando />;

  return usuario.tipoAcceso === 'SOLICITANTE' ? (
    <DashboardSolicitante nombre={usuario.nombreCompleto} email={usuario.email} razonSocial={usuario.razonSocial} />
  ) : (
    <DashboardOperativo />
  );
}

function DashboardSolicitante({
  nombre,
  email,
  razonSocial,
}: {
  nombre: string;
  email: string | null;
  razonSocial: string;
}): JSX.Element {
  const navegar = useNavigate();
  const conteo = (estado: EstadoSolicitud) =>
    useQuery({
      queryKey: ['dashboard', 'solicitudes-conteo', estado],
      queryFn: () => api.get<Pagina<Solicitud>>(`/solicitudes${consulta({ estado, tamano: 25, pagina: 1 })}`),
    });

  const pendientes = conteo('PENDIENTE');
  const aprobadas = conteo('APROBADA');
  const declinadas = conteo('DECLINADA');
  const errorConteos = pendientes.error ?? aprobadas.error ?? declinadas.error;

  const { data: recientes, isLoading, isError, error } = useQuery({
    queryKey: ['dashboard', 'solicitudes-recientes'],
    queryFn: () => api.get<Pagina<Solicitud>>(`/solicitudes${consulta({ tamano: 25, pagina: 1 })}`),
  });

  const esMovil = useEsMovil();

  return (
    <>
      <div className="cabecera-pagina">
        <div>
          <h1>Dashboard</h1>
          <p className="cabecera-subtitulo">{razonSocial}</p>
        </div>
        <Link className="boton" to="/solicitudes/nueva">
          Crear solicitud
        </Link>
      </div>

      {errorConteos ? (
        <Aviso tono="error">{mensajeError(errorConteos, 'No se pudieron cargar los contadores de solicitudes.')}</Aviso>
      ) : null}

      <div className="rejilla-indicadores">
        <Indicador titulo="MIS DATOS" valor={nombre} nota={email ?? undefined} />
        <Indicador
          titulo={ETIQUETA_ESTADO_SOLICITUD.PENDIENTE.toUpperCase()}
          valor={pendientes.data?.total ?? 0}
          to="/solicitudes?estado=PENDIENTE"
        />
        <Indicador
          titulo={ETIQUETA_ESTADO_SOLICITUD.APROBADA.toUpperCase()}
          valor={aprobadas.data?.total ?? 0}
          to="/solicitudes?estado=APROBADA"
          className="col-ocultar-movil"
        />
        <Indicador
          titulo={ETIQUETA_ESTADO_SOLICITUD.DECLINADA.toUpperCase()}
          valor={declinadas.data?.total ?? 0}
          to="/solicitudes?estado=DECLINADA"
          className="col-ocultar-movil"
        />
      </div>

      <div className="panel">
        <div className="panel-cabecera tono-solicitud">
          <h2>Mis últimas solicitudes</h2>
          <Link to="/solicitudes">Ver todas</Link>
        </div>
        <div className="panel-cuerpo">
          {isLoading ? <Cargando /> : null}
          {isError ? <Aviso tono="error">{mensajeError(error, 'No se pudieron cargar las solicitudes.')}</Aviso> : null}
          {recientes && recientes.datos.length === 0 ? (
            <Vacio titulo="Aún no has creado solicitudes" detalle="Usa el botón «Crear solicitud» para empezar." />
          ) : null}
          {recientes && recientes.datos.length > 0 ? (
            esMovil ? (
              <div className="tarjetas-ot">
                {recientes.datos.slice(0, 5).map((s) => (
                  <div
                    key={s.id}
                    className="tarjeta-ot"
                    role="button"
                    tabIndex={0}
                    onClick={() => navegar(`/solicitudes/${s.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') navegar(`/solicitudes/${s.id}`);
                    }}
                  >
                    <div className="tarjeta-ot-cuerpo">
                      <div className="tarjeta-ot-cabecera">
                        <span className="numero-registro">#{s.numero}</span>
                        <EstadoSolicitudEtiqueta estado={s.estado} />
                      </div>
                      <div className="tarjeta-ot-titulo">{s.titulo}</div>
                      <div className="tarjeta-ot-datos">
                        <span>{formatearFechaHora(s.solicitadoEl)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tabla-contenedor">
                <table className="tabla">
                  <thead>
                    <tr>
                      <th>N.º</th>
                      <th>Título</th>
                      <th>Estado</th>
                      <th>Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recientes.datos.slice(0, 5).map((s) => (
                      <tr key={s.id}>
                        <td>
                          <Link to={`/solicitudes/${s.id}`}>#{s.numero}</Link>
                        </td>
                        <td>
                          <Link to={`/solicitudes/${s.id}`}>{s.titulo}</Link>
                        </td>
                        <td>
                          <EstadoSolicitudEtiqueta estado={s.estado} />
                        </td>
                        <td>{formatearFechaHora(s.solicitadoEl)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : null}
        </div>
      </div>
    </>
  );
}

function DashboardOperativo(): JSX.Element {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const esMovil = useEsMovil();

  const {
    data: resumenOrdenes,
    isError: errorResumen,
    error: errorResumenDetalle,
  } = useQuery({
    // Mismo criterio que la vista inicial de Órdenes de trabajo: OT creadas desde
    // el primer día del mes (hora de Lima), para que los contadores coincidan.
    queryKey: ['dashboard', 'resumen-ordenes'],
    queryFn: () => api.get<ResumenOrdenes>(`/ordenes-trabajo/resumen${consulta({ creadoDesde: primerDiaDelMesLima() })}`),
  });

  const { data: bandeja, isError: errorBandeja, error: errorBandejaDetalle } = useQuery({
    queryKey: ['bandeja-resumen'],
    queryFn: () => api.get<{ estado: EstadoSolicitud; total: number; antiguas: number }[]>('/solicitudes/bandeja'),
    enabled: puede('solicitud:leer_todas'),
  });

  const { data: observacionesActivoPendientes, isError: errorObsActivo } = useQuery({
    queryKey: ['observaciones-activo-pendientes'],
    queryFn: () => api.get<unknown[]>('/activos/observaciones/pendientes'),
    enabled: puede('activo:leer'),
  });

  const { data: observacionesUbicacionPendientes, isError: errorObsUbicacion } = useQuery({
    queryKey: ['observaciones-ubicacion-pendientes'],
    queryFn: () => api.get<unknown[]>('/ubicaciones/observaciones/pendientes'),
    enabled: puede('ubicacion:leer'),
  });

  const { data: observacionesSujetoPendientes, isError: errorObsSujeto } = useQuery({
    queryKey: ['observaciones-sujeto-pendientes'],
    queryFn: () => api.get<unknown[]>('/sujetos-evaluables/observaciones/pendientes'),
    enabled: puede('sujeto_evaluable:leer'),
  });

  const {
    data: ordenesVencidas,
    isLoading: cargandoVencidas,
    isError: errorVencidas,
    error: errorVencidasDetalle,
  } = useQuery({
    queryKey: ['dashboard', 'ordenes-vencidas'],
    queryFn: () =>
      api.get<Pagina<OrdenTrabajo>>(
        `/ordenes-trabajo${consulta({ vencidas: true, tamano: 25, pagina: 1, orden: 'numero', direccion: 'desc' })}`,
      ),
  });

  const {
    data: solicitudesPendientes,
    isLoading: cargandoPendientes,
    isError: errorPendientes,
    error: errorPendientesDetalle,
  } = useQuery({
    queryKey: ['dashboard', 'solicitudes-pendientes'],
    queryFn: () => api.get<Pagina<Solicitud>>(`/solicitudes${consulta({ estado: 'PENDIENTE', tamano: 25, pagina: 1 })}`),
    enabled: puede('solicitud:leer_todas'),
  });

  const ordenesPendientes = (resumenOrdenes?.porEstado ?? [])
    .filter((p) => (ESTADOS_OT_ABIERTOS as readonly string[]).includes(p.estado))
    .reduce((total, p) => total + p.cantidad, 0);

  const solicitudesPorAprobar = bandeja?.find((b) => b.estado === 'PENDIENTE')?.total ?? 0;
  const solicitudesDeclinadas = bandeja?.find((b) => b.estado === 'DECLINADA')?.total ?? 0;
  const solicitudesAntiguas = bandeja?.find((b) => b.estado === 'PENDIENTE')?.antiguas ?? 0;
  const alertasActivos =
    (observacionesActivoPendientes?.length ?? 0) +
    (observacionesUbicacionPendientes?.length ?? 0) +
    (observacionesSujetoPendientes?.length ?? 0);

  const panelOrdenesVencidas = (
    <div className="panel" key="ordenes-vencidas">
      <div className="panel-cabecera tono-ot">
        <h2>Órdenes de trabajo vencidas</h2>
        <EnlaceRuta to="/ordenes">Ver todas</EnlaceRuta>
      </div>
      <div className="panel-cuerpo">
        {cargandoVencidas ? <Cargando /> : null}
        {errorVencidas ? (
          <Aviso tono="error">{mensajeError(errorVencidasDetalle, 'No se pudieron cargar las órdenes vencidas.')}</Aviso>
        ) : null}
        {errorResumen ? (
          <Aviso tono="error">{mensajeError(errorResumenDetalle, 'No se pudo cargar el resumen de órdenes.')}</Aviso>
        ) : null}
        {ordenesVencidas && ordenesVencidas.datos.length === 0 ? (
          <Vacio titulo="No hay órdenes vencidas" detalle="Todo está al día." />
        ) : null}
        {resumenOrdenes ? <GraficoVencidasPorPrioridad datos={resumenOrdenes.vencidasPorPrioridad} /> : null}
        {resumenOrdenes ? <GraficoLineaVencidas datos={resumenOrdenes.vencidasPorPrioridad} /> : null}
      </div>
    </div>
  );

  const panelSolicitudes = puede('solicitud:leer_todas') ? (
    <div className="panel" key="solicitudes-por-aprobar">
      <div className="panel-cabecera tono-solicitud">
        <h2>Solicitudes por aprobar</h2>
        <Link to="/solicitudes">Ver todas</Link>
      </div>
      <div className="panel-cuerpo">
        {cargandoPendientes ? <Cargando /> : null}
        {errorPendientes ? (
          <Aviso tono="error">{mensajeError(errorPendientesDetalle, 'No se pudieron cargar las solicitudes pendientes.')}</Aviso>
        ) : null}
        {errorBandeja ? (
          <Aviso tono="error">{mensajeError(errorBandejaDetalle, 'No se pudo cargar la bandeja de solicitudes.')}</Aviso>
        ) : null}
        {solicitudesPendientes && solicitudesPendientes.datos.length === 0 ? (
          <Vacio titulo="No hay solicitudes pendientes" detalle="La bandeja está vacía." />
        ) : null}
        {solicitudesPendientes && solicitudesPendientes.datos.length > 0 ? (
          esMovil ? (
            <div className="tarjetas-ot">
              {solicitudesPendientes.datos.slice(0, 8).map((s) => (
                <div
                  key={s.id}
                  className="tarjeta-ot"
                  role="button"
                  tabIndex={0}
                  onClick={() => navegar(`/solicitudes/${s.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navegar(`/solicitudes/${s.id}`);
                  }}
                >
                  <div className="tarjeta-ot-cuerpo">
                    <div className="tarjeta-ot-cabecera">
                      <span className="numero-registro">#{s.numero}</span>
                      <PrioridadEtiqueta prioridad={s.prioridad} />
                    </div>
                    <div className="tarjeta-ot-titulo">{s.titulo}</div>
                    <div className="tarjeta-ot-datos">
                      {s.ubicacion ? <span>{s.ubicacion}</span> : null}
                      <span>{formatearFechaHora(s.solicitadoEl)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="tabla-contenedor">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>N.º</th>
                    <th>Título</th>
                    <th>Prioridad</th>
                    <th className="col-ocultar-movil">Ubicación</th>
                    <th>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {solicitudesPendientes.datos.slice(0, 8).map((s) => (
                    <tr key={s.id}>
                      <td>
                        <Link to={`/solicitudes/${s.id}`}>#{s.numero}</Link>
                      </td>
                      <td>
                        <Link to={`/solicitudes/${s.id}`}>{s.titulo}</Link>
                      </td>
                      <td>
                        <PrioridadEtiqueta prioridad={s.prioridad} />
                      </td>
                      <td className="col-ocultar-movil">
                        <EnlaceUbicacion id={s.ubicacionId} nombre={s.ubicacion} />
                      </td>
                      <td>{formatearFechaHora(s.solicitadoEl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className="cabecera-pagina">
        <div>
          <h1>Dashboard</h1>
          <p className="cabecera-subtitulo">Estado operativo de la organización.</p>
        </div>
        <div className="acciones-cabecera">
          {puede('solicitud:leer_todas') ? (
            <Link className="boton secundario" to="/solicitudes" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <IconoSolicitud /> Ver solicitudes
            </Link>
          ) : null}
          {puede('ot:crear') ? (
            <EnlaceRuta className="boton" to="/ordenes/nueva" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <IconoMas /> Nueva orden de trabajo
            </EnlaceRuta>
          ) : null}
        </div>
      </div>

      {errorObsActivo || errorObsUbicacion || errorObsSujeto ? (
        <Aviso tono="error">No se pudieron cargar las observaciones pendientes de activos/ubicaciones.</Aviso>
      ) : null}

      <div className="rejilla-indicadores">
        <Indicador
          titulo="OT ABIERTAS/EN CURSO"
          valor={ordenesPendientes}
          nota="Requieren seguimiento"
          to="/ordenes"
          tono="azul"
        />
        <Indicador
          titulo="OT VENCIDAS"
          valor={resumenOrdenes?.vencidas ?? 0}
          nota={resumenOrdenes?.vencidas ? 'Requieren atención' : 'Al día'}
          to="/ordenes?vencidas=true"
          tono={resumenOrdenes?.vencidas ? 'roja' : 'verde'}
        />
        {puede('solicitud:leer_todas') ? (
          <Indicador
            titulo="SOLICITUDES POR APROBAR"
            valor={solicitudesPorAprobar}
            nota={solicitudesAntiguas ? `${solicitudesAntiguas} llevan demasiado tiempo sin resolver` : undefined}
            to="/solicitudes?estado=PENDIENTE"
            tono="ambar"
            className="col-ocultar-movil"
          />
        ) : null}
        {puede('solicitud:leer_todas') ? (
          <Indicador
            titulo="SOLICITUDES DECLINADAS"
            valor={solicitudesDeclinadas}
            to="/solicitudes?estado=DECLINADA"
            tono="gris"
            className="col-ocultar-movil"
          />
        ) : null}
        {puede('activo:leer') || puede('ubicacion:leer') || puede('sujeto_evaluable:leer') ? (
          <Indicador
            titulo="OBSERVACIONES"
            valor={alertasActivos}
            nota={alertasActivos ? 'Sin resolver' : 'Sin pendientes'}
            to="/activos/alertas"
            tono={alertasActivos ? 'ambar' : 'verde'}
            className="col-ocultar-movil"
          />
        ) : null}
      </div>

      {/* Cada rejilla es una fila; el wrapper da el mismo espacio entre filas que entre columnas. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div className="rejilla-columnas" style={{ alignItems: 'stretch' }}>
          {puede('ot:leer') ? <PanelRankingSedes /> : null}
          <PanelPorTipo />
        </div>

        <div className="rejilla-columnas" style={{ alignItems: 'stretch' }}>
          {panelOrdenesVencidas}
          {panelSolicitudes}
        </div>

        <div className="rejilla-columnas" style={{ alignItems: 'stretch' }}>
          {puede('ot:leer') ? <PanelActivosReincidentes /> : null}
        </div>
      </div>
    </>
  );
}

interface ActivoReincidente {
  activoId: string;
  numero: number;
  nombre: string;
  sede: string | null;
  ordenes: number;
  abiertas: number;
}

/** Top 5 de activos con más OT reactivas, en tabla como el ranking de sedes. */
function PanelActivosReincidentes(): JSX.Element {
  const navegar = useNavigate();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['dashboard', 'activos-reincidentes'],
    queryFn: () => api.get<ActivoReincidente[]>('/ordenes-trabajo/activos-reincidentes'),
  });

  return (
    <div className="panel">
      <div className="panel-cabecera tono-ot">
        <h2>Activos más reincidentes</h2>
        <span className="campo-nota">Top 5 por OT reactivas</span>
      </div>
      <div className="panel-cuerpo">
        {isLoading ? <Cargando /> : null}
        {isError ? <Aviso tono="error">{mensajeError(error, 'No se pudo cargar los activos reincidentes.')}</Aviso> : null}
        {data && data.length === 0 ? <Vacio titulo="Sin reincidencias" detalle="No hay OT reactivas registradas." /> : null}
        {data && data.length > 0 ? (
          <div className="ranking-tabla-contenedor">
            <table className="ranking-tabla">
              <thead>
                <tr>
                  <th style={{ width: 64 }}>Puesto</th>
                  <th>Activo</th>
                  <th>Sede</th>
                  <th className="ranking-num">OT reactivas</th>
                  <th className="ranking-num">Abiertas</th>
                </tr>
              </thead>
              <tbody>
                {data.map((a, i) => (
                  <tr
                    key={a.activoId}
                    onClick={() => navegar(`/activos/${a.activoId}`)}
                    className="ranking-fila"
                    title="Ver el activo"
                  >
                    <td>
                      <span className={`ranking-puesto${i < 3 ? ` ranking-puesto-${i + 1}` : ''}`}>{i + 1}</span>
                    </td>
                    <td>
                      <span className="ranking-sede">
                        #{a.numero} {a.nombre}
                      </span>
                    </td>
                    <td>{a.sede ?? '—'}</td>
                    <td className="ranking-num">
                      <span className="ranking-pill ranking-pill-ot">{a.ordenes}</span>
                    </td>
                    <td className="ranking-num">
                      <span className={`ranking-pill ${a.abiertas > 0 ? 'ranking-pill-solicitud' : 'ranking-pill-cero'}`}>
                        {a.abiertas}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}

interface SolicitudPorTipo {
  id: string;
  numero: number;
  titulo: string;
  estado: EstadoSolicitud;
  ubicacion: string | null;
  solicitadoEl: string;
}

interface FilaPorTipo {
  clave: string;
  tipo: 'Solicitud' | 'OT';
  numero: number;
  titulo: string;
  ubicacion: string | null;
  estado: string;
  fecha: string | null;
  ruta: string;
}

/**
 * Una sola tabla con todo lo de los tipos de activo elegidos: solicitudes y OT
 * abiertas de esos tipos. La selección es la misma que se guarda con el engranaje.
 */
// 10 filas llenan el alto del ranking de sedes, para que la tarjeta no deje un hueco debajo.
const TAMANO_PAGINA_TIPO = 10;

function PanelPorTipo(): JSX.Element {
  const navegar = useNavigate();
  const { puede } = useSesion();
  const [pagina, setPagina] = useState(1);

  const { data: preferencia } = useQuery({
    queryKey: ['preferencias', 'ots-por-tipo'],
    queryFn: () => api.get<{ tipoActivoId: string[] }>('/preferencias/dashboard/ots-por-tipo'),
  });
  const tipos = preferencia?.tipoActivoId ?? [];
  const hayTipos = tipos.length > 0;
  const lista = tipos.join(',');

  const solicitudes = useQuery({
    queryKey: ['dashboard', 'solicitudes-por-tipo', tipos],
    // Solo las que faltan aprobar: ni declinadas ni ya aprobadas.
    queryFn: () =>
      api.get<Pagina<SolicitudPorTipo>>(`/solicitudes?estado=PENDIENTE&tamano=25&pagina=1&tipoActivoId=${lista}`),
    enabled: hayTipos && puede('solicitud:leer'),
  });

  const ots = useQuery({
    queryKey: ['dashboard', 'ordenes-abiertas', tipos],
    queryFn: () =>
      api.get<Pagina<OrdenTrabajo>>(
        `/ordenes-trabajo?estado=${ESTADOS_OT_ABIERTOS.join(',')}&tipoActivoId=${lista}&tamano=25&pagina=1&orden=numero&direccion=desc`,
      ),
    enabled: hayTipos,
  });

  const filas: FilaPorTipo[] = [
    ...(solicitudes.data?.datos ?? []).map((s) => ({
      clave: `s-${s.id}`,
      tipo: 'Solicitud' as const,
      numero: s.numero,
      titulo: s.titulo,
      ubicacion: s.ubicacion,
      estado: ETIQUETA_ESTADO_SOLICITUD[s.estado],
      fecha: s.solicitadoEl,
      ruta: `/solicitudes/${s.id}`,
    })),
    ...(ots.data?.datos ?? []).map((o) => ({
      clave: `o-${o.id}`,
      tipo: 'OT' as const,
      numero: o.numero,
      titulo: o.titulo,
      ubicacion: o.ubicacion,
      estado: ETIQUETA_ESTADO_OT[o.estado],
      fecha: o.fechaVencimiento,
      ruta: `/ordenes/${o.id}`,
    })),
  ];

  const cargando = solicitudes.isLoading || ots.isLoading;
  const error = solicitudes.isError || ots.isError;

  // Tamaño fijo: siempre se pintan TAMANO_PAGINA filas, con relleno si faltan, para que el panel no cambie de alto.
  const paginas = Math.max(1, Math.ceil(filas.length / TAMANO_PAGINA_TIPO));
  const pagActual = Math.min(pagina, paginas);
  const visibles = filas.slice((pagActual - 1) * TAMANO_PAGINA_TIPO, pagActual * TAMANO_PAGINA_TIPO);
  const relleno = Math.max(0, TAMANO_PAGINA_TIPO - visibles.length);

  return (
    <div className="panel" style={{ display: 'flex', flexDirection: 'column' }}>
      <div
        className="panel-cabecera tono-ot"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <h2>Por tipo de activo</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <EnlaceRuta to="/ordenes">Ver OT</EnlaceRuta>
          {puede('solicitud:leer') && hayTipos ? (
            <button
              type="button"
              className="boton-enlace"
              onClick={() => navegar(`/solicitudes?estado=PENDIENTE&tipoActivoId=${lista}`)}
            >
              Ver solicitudes
            </button>
          ) : null}
          <EngranajeTipos seccion="ots-por-tipo" seleccion={tipos} />
        </div>
      </div>
      <div className="panel-cuerpo" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {!hayTipos ? (
          <Vacio titulo="Sin tipos elegidos" detalle="Usa el engranaje para elegir qué tipos de activo mostrar." />
        ) : null}
        {hayTipos && cargando ? <Cargando /> : null}
        {hayTipos && error ? <Aviso tono="error">No se pudo cargar lo de estos tipos de activo.</Aviso> : null}
        {hayTipos && !cargando && !error && filas.length === 0 ? (
          <Vacio titulo="Nada pendiente" detalle="No hay solicitudes ni OT abiertas de estos tipos." />
        ) : null}
        {filas.length > 0 ? (
          <div className="ranking-tabla-contenedor">
            <table className="ranking-tabla">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>N.º</th>
                  <th>Título</th>
                  <th>Ubicación</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((f) => (
                  <tr key={f.clave} className="ranking-fila" onClick={() => navegar(f.ruta)}>
                    <td>
                      <span className={`ranking-pill ${f.tipo === 'OT' ? 'ranking-pill-ot' : 'ranking-pill-solicitud'}`}>
                        {f.tipo}
                      </span>
                    </td>
                    <td>#{f.numero}</td>
                    <td>{f.titulo}</td>
                    <td>{f.ubicacion ?? '—'}</td>
                    <td>{f.estado}</td>
                    <td>{f.fecha ? formatearFechaHora(f.fecha) : '—'}</td>
                  </tr>
                ))}
                {Array.from({ length: relleno }, (_, i) => (
                  <tr key={`relleno-${i}`} aria-hidden="true" className="ranking-fila-relleno">
                    <td colSpan={6}>&nbsp;</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {filas.length > TAMANO_PAGINA_TIPO ? (
          <div style={{ marginTop: 'auto' }}>
            <Paginacion pagina={pagActual} tamano={TAMANO_PAGINA_TIPO} total={filas.length} onCambiar={setPagina} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Engranaje de un panel del dashboard: abre la lista de tipos de activo con
 * casillas y guarda la elección como preferencia del usuario en `seccion`.
 */
function EngranajeTipos({ seccion, seleccion }: { seccion: string; seleccion: string[] }): JSX.Element {
  const cliente = useQueryClient();
  const [editando, setEditando] = useState(false);
  const [borrador, setBorrador] = useState<string[]>([]);
  const { data: catalogoTipos } = useTiposActivo(editando);

  const guardar = useMutation({
    mutationFn: (ids: string[]) =>
      api.put<{ tipoActivoId: string[] }>(`/preferencias/dashboard/${seccion}`, { tipoActivoId: ids }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['preferencias', seccion] });
      setEditando(false);
    },
  });

  function abrirEdicion(): void {
    setBorrador(seleccion);
    setEditando(true);
  }

  function alternarBorrador(id: string): void {
    setBorrador((actual) => (actual.includes(id) ? actual.filter((t) => t !== id) : [...actual, id]));
  }

  return (
    <>
      <button type="button" className="boton-icono" aria-label="Elegir tipos de activo" title="Elegir tipos" onClick={abrirEdicion}>
        <IconoEngranaje />
      </button>

      {editando ? (
        <div className="capa-dialogo" role="presentation" onClick={(e) => e.target === e.currentTarget && setEditando(false)}>
          <div className="panel" role="dialog" aria-label="Tipos de activo a mostrar" style={{ maxWidth: 520, width: '100%' }}>
            <div className="panel-cabecera">
              <h2>Tipos de activo a mostrar</h2>
              <button type="button" className="boton-enlace" onClick={() => setEditando(false)}>
                Cancelar
              </button>
            </div>
            <div className="panel-cuerpo">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, maxHeight: 360, overflowY: 'auto' }}>
                {(catalogoTipos ?? []).map((t) => (
                  <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={borrador.includes(t.id)} onChange={() => alternarBorrador(t.id)} />
                    {t.nombre}
                  </label>
                ))}
              </div>
              <div className="acciones-cabecera" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="boton" disabled={guardar.isPending} onClick={() => guardar.mutate(borrador)}>
                  Guardar
                </button>
              </div>
              {guardar.isError ? <Aviso tono="error">{mensajeError(guardar.error, 'No se pudo guardar la preferencia.')}</Aviso> : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

const COLOR_PRIORIDAD: Record<Prioridad, string> = {
  ALTA: 'var(--prioridad-alta)',
  MEDIA: 'var(--prioridad-media)',
  BAJA: 'var(--prioridad-baja)',
  NINGUNA: 'var(--prioridad-ninguna)',
};

const ORDEN_PRIORIDAD: Prioridad[] = ['ALTA', 'MEDIA', 'BAJA', 'NINGUNA'];

interface FilaVencidas {
  prioridad: Prioridad;
  etiqueta: string;
  cantidad: number;
}

/**
 * Barra + punto de remate en un solo trazo, dibujados juntos como `shape` del
 * propio `Bar`: así el punto queda garantizado en la misma coordenada que el
 * tope de la barra, sin depender de alinear dos series (`Bar` + `Line`) por
 * separado. El punto retoma el lenguaje visual de Medidores (`dot` sobre la
 * línea de tendencia), para que ambos gráficos se sientan del mismo sistema.
 */
function BarraConPunto(props: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: FilaVencidas;
}): JSX.Element | null {
  const { x, y, width, height, payload } = props;
  if (x == null || y == null || width == null || height == null || !payload) return null;
  const color = COLOR_PRIORIDAD[payload.prioridad];
  const cx = x + width / 2;
  const radio = Math.min(8, width / 2);

  return (
    <g>
      <path
        d={`M${x},${y + height} L${x},${y + radio} A${radio},${radio} 0 0 1 ${x + radio},${y}
            L${x + width - radio},${y} A${radio},${radio} 0 0 1 ${x + width},${y + radio}
            L${x + width},${y + height} Z`}
        fill={color}
        fillOpacity={0.16}
        stroke={color}
        strokeWidth={1.5}
      />
      <circle cx={cx} cy={y} r={6} fill={color} stroke="var(--panel)" strokeWidth={2} />
      <text x={cx} y={y - 12} textAnchor="middle" fontSize={13} fontWeight={700} fill="var(--texto)">
        {payload.cantidad}
      </text>
    </g>
  );
}

/** Barras verticales de OT vencidas por prioridad, rematadas con un punto — de un vistazo, cuánto es urgente. */
function GraficoVencidasPorPrioridad({ datos }: { datos: ResumenOrdenes['vencidasPorPrioridad'] }): JSX.Element | null {
  const total = datos.reduce((suma, d) => suma + d.cantidad, 0);
  if (total === 0) return null;

  const porPrioridad = new Map(datos.map((d) => [d.prioridad, d.cantidad]));
  const filas: FilaVencidas[] = ORDEN_PRIORIDAD.filter((p) => (porPrioridad.get(p) ?? 0) > 0).map((prioridad) => ({
    prioridad,
    etiqueta: ETIQUETA_PRIORIDAD[prioridad],
    cantidad: porPrioridad.get(prioridad) ?? 0,
  }));
  const maximo = Math.max(...filas.map((f) => f.cantidad));

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--texto-medio)' }}>POR PRIORIDAD</span>
        <span style={{ fontSize: 12, color: 'var(--texto-tenue)' }}>
          {total} vencida{total === 1 ? '' : 's'}
        </span>
      </div>
      <div style={{ height: 210 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={filas} barSize={44} margin={{ top: 30, right: 12, left: 12, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="var(--borde-suave)" />
            <XAxis
              dataKey="etiqueta"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 13, fontWeight: 600, fill: 'var(--texto-medio)' }}
            />
            {/* Domina con holgura por encima del máximo: dejar sitio, dentro
                del propio área de trazado, para el punto y la etiqueta de la
                barra más alta — si tocaran el borde del `clipPath`, se cortarían. */}
            <YAxis hide type="number" domain={[0, Math.ceil(maximo * 1.25) + 1]} allowDataOverflow />
            <Tooltip
              cursor={{ fill: 'var(--lienzo)' }}
              formatter={(valor) => [String(valor ?? 0), 'OT vencidas']}
              contentStyle={{
                background: 'var(--panel)',
                border: '1px solid var(--borde)',
                borderRadius: 8,
                fontSize: 12.5,
              }}
            />
            <Bar dataKey="cantidad" shape={BarraConPunto} isAnimationActive animationDuration={500} maxBarSize={44} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/** Misma paleta, pero pensada para el trazo de una línea (sin relleno). */
function lineaPrioridad(prioridad: Prioridad): string {
  return COLOR_PRIORIDAD[prioridad];
}

/**
 * Tendencia de OT vencidas por prioridad, con el mismo lenguaje visual que el
 * gráfico de lecturas de Medidores: grilla punteada, línea suave y puntos
 * marcados. Complementa al de barras — ese responde "cuánto", este ayuda a
 * leer la progresión Alta → Ninguna de un vistazo.
 */
function GraficoLineaVencidas({ datos }: { datos: ResumenOrdenes['vencidasPorPrioridad'] }): JSX.Element | null {
  const total = datos.reduce((suma, d) => suma + d.cantidad, 0);
  if (total === 0) return null;

  const porPrioridad = new Map(datos.map((d) => [d.prioridad, d.cantidad]));
  const filas: FilaVencidas[] = ORDEN_PRIORIDAD.map((prioridad) => ({
    prioridad,
    etiqueta: ETIQUETA_PRIORIDAD[prioridad],
    cantidad: porPrioridad.get(prioridad) ?? 0,
  }));

  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--texto-medio)' }}>TENDENCIA POR PRIORIDAD</span>
        <span style={{ fontSize: 12, color: 'var(--texto-tenue)' }}>
          {total} vencida{total === 1 ? '' : 's'}
        </span>
      </div>
      <div style={{ height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={filas} margin={{ left: 8, right: 16, top: 16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--borde)" />
            <XAxis
              dataKey="etiqueta"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12.5, fontWeight: 600, fill: 'var(--texto-medio)' }}
            />
            <YAxis allowDecimals={false} width={28} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(valor) => [String(valor ?? 0), 'OT vencidas']} />
            <Line
              type="monotone"
              dataKey="cantidad"
              stroke="var(--azul)"
              strokeWidth={2}
              dot={(props) => {
                const { key, cx, cy, payload } = props as {
                  key?: React.Key;
                  cx?: number;
                  cy?: number;
                  payload?: FilaVencidas;
                };
                if (cx == null || cy == null || !payload) return <g key={key} />;
                return <circle key={key} cx={cx} cy={cy} r={5} fill={lineaPrioridad(payload.prioridad)} stroke="var(--panel)" strokeWidth={2} />;
              }}
              activeDot={{ r: 7 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/** Enlaza a la ficha de la ubicación cuando el rol puede verla; si no, solo muestra el nombre. */
function EnlaceUbicacion({ id, nombre }: { id: string | null; nombre: string | null }): JSX.Element {
  const { puede } = useSesion();
  if (!nombre) return <>—</>;
  if (!id || !puede('ubicacion:leer')) return <>{nombre}</>;
  return <EnlaceRuta to={`/ubicaciones/${id}`}>{nombre}</EnlaceRuta>;
}

interface RankingSede {
  sedeId: string;
  nombre: string;
  solicitudes: number;
  solicitudesTodas: number;
  solicitudesHoy: number;
  ordenes: number;
  vencidas: number;
  total: number;
}

interface ActivasSede {
  ordenes: {
    id: string;
    numero: number;
    titulo: string;
    estado: EstadoOT;
    vencida: boolean;
    ubicacion: string | null;
    fotoUrl: string | null;
  }[];
  solicitudes: { id: string; numero: number; titulo: string; ubicacion: string | null; fotoUrl: string | null }[];
}

interface SolicitudDeSede {
  id: string;
  numero: number;
  titulo: string;
  estado: EstadoSolicitud;
  ubicacion: string | null;
  fotoUrl: string | null;
  ordenes: { id: string; numero: number; titulo: string; estado: EstadoOT; fotoUrl: string | null }[];
}

/** Sin pintar nada: solo registra un cierre (Escape y botón atrás) mientras está montado. */
function CierreAtras({ alCerrar }: { alCerrar: () => void }): null {
  useCierreDialogo(alCerrar);
  return null;
}

/** Última sede y pestaña abiertas en el ranking: se recuerdan mientras dure la sesión, para no perderlas al volver desde una OT. */
let panelSedesGuardado: { sedeId: string | null; pestana: 'solicitudes' | 'ots' } = { sedeId: null, pestana: 'solicitudes' };

/** Ranking de sedes en tabla; cada fila abre un panel lateral con sus solicitudes y OT. */
function PanelRankingSedes(): JSX.Element {
  const navegar = useNavigate();
  const [sedeId, setSedeId] = useState<string | null>(panelSedesGuardado.sedeId);
  const [pestana, setPestana] = useState<'solicitudes' | 'ots'>(panelSedesGuardado.pestana);
  useEffect(() => {
    panelSedesGuardado = { sedeId, pestana };
  });

  const { data: ranking, isLoading, isError, error } = useQuery({
    queryKey: ['dashboard', 'ranking-sedes'],
    queryFn: () => api.get<RankingSede[]>('/ordenes-trabajo/ranking-sedes'),
  });

  const sedeElegida = ranking?.find((s) => s.sedeId === sedeId) ?? null;

  const { data: activas } = useQuery({
    queryKey: ['dashboard', 'sede-activas', sedeId],
    queryFn: () => api.get<ActivasSede>(`/ordenes-trabajo/sedes/${sedeId}/activas`),
    enabled: sedeId !== null,
  });

  const { data: solicitudes, isLoading: cargandoSolicitudes } = useQuery({
    queryKey: ['dashboard', 'sede-solicitudes', sedeId],
    queryFn: () => api.get<SolicitudDeSede[]>(`/ordenes-trabajo/sedes/${sedeId}/solicitudes`),
    enabled: sedeId !== null,
  });

  function abrirSede(id: string): void {
    setPestana('solicitudes');
    setSedeId(id);
  }

  useEffect(() => {
    if (sedeId === null) return;
    const alTeclear = (e: KeyboardEvent) => e.key === 'Escape' && setSedeId(null);
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [sedeId]);

  return (
    <div className="panel">
      <div className="panel-cabecera tono-ot">
        <h2>Ranking de sedes</h2>
        <span className="campo-nota">Solicitudes pendientes y OT abiertas</span>
      </div>
      <div className="panel-cuerpo">
        {isLoading ? <Cargando /> : null}
        {isError ? <Aviso tono="error">{mensajeError(error, 'No se pudo cargar el ranking de sedes.')}</Aviso> : null}
        {ranking && ranking.length === 0 ? (
          <Vacio titulo="Sin pendientes por sede" detalle="No hay solicitudes ni OT abiertas." />
        ) : null}
        {ranking && ranking.length > 0 ? (
          <div className="ranking-tabla-contenedor">
            <table className="ranking-tabla">
              <thead>
                <tr>
                  <th style={{ width: 64 }}>Puesto</th>
                  <th>Sede</th>
                  <th className="ranking-num">Solicitudes</th>
                  <th className="ranking-num">Solicitudes creadas hoy</th>
                  <th className="ranking-num">OT abiertas</th>
                  <th className="ranking-num">Total</th>
                </tr>
              </thead>
              <tbody>
                {ranking.map((s, i) => (
                  <tr
                    key={s.sedeId}
                    onClick={() => abrirSede(s.sedeId)}
                    className="ranking-fila"
                    title="Ver las solicitudes y OT de esta sede"
                  >
                    <td>
                      <span className={`ranking-puesto${i < 3 ? ` ranking-puesto-${i + 1}` : ''}`}>{i + 1}</span>
                    </td>
                    <td>
                      <span className="ranking-sede">{s.nombre}</span>
                    </td>
                    <td className="ranking-num">
                      <span className={`ranking-pill ${s.solicitudes > 0 ? 'ranking-pill-solicitud' : 'ranking-pill-cero'}`}>
                        {s.solicitudes}
                      </span>
                    </td>
                    <td className="ranking-num">
                      <span className={`ranking-pill ${s.solicitudesHoy > 0 ? 'ranking-pill-solicitud' : 'ranking-pill-cero'}`}>
                        {s.solicitudesHoy}
                      </span>
                    </td>
                    <td className="ranking-num">
                      <span className={`ranking-pill ${s.ordenes > 0 ? 'ranking-pill-ot' : 'ranking-pill-cero'}`}>{s.ordenes}</span>
                    </td>
                    <td className="ranking-num">
                      <span className={`ranking-total${s.total > 0 ? ' ranking-total-activo' : ''}`}>{s.total}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {sedeId && sedeElegida ? (
        <div className="capa-dialogo" role="presentation" onClick={(e) => e.target === e.currentTarget && setSedeId(null)}>
          {/* Registra el panel para que el botón atrás del celular lo cierre (y no cierre la app). */}
          <CierreAtras alCerrar={() => setSedeId(null)} />
          <aside className="panel sede-panel-lateral" role="dialog" aria-label={`Pendientes de ${sedeElegida.nombre}`}>
            <div className="panel-cabecera">
              <h2>{sedeElegida.nombre}</h2>
              <button type="button" className="boton-enlace" onClick={() => setSedeId(null)}>
                Cerrar
              </button>
            </div>
            <div className="panel-cuerpo">
              <div className="tabs-conflicto" role="tablist" aria-label="Agrupar por">
                <button
                  type="button"
                  role="tab"
                  aria-selected={pestana === 'solicitudes'}
                  className={pestana === 'solicitudes' ? 'activa' : ''}
                  onClick={() => setPestana('solicitudes')}
                >
                  Solicitudes ({solicitudes ? solicitudes.length : '…'})
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={pestana === 'ots'}
                  className={pestana === 'ots' ? 'activa' : ''}
                  onClick={() => setPestana('ots')}
                >
                  OT ({activas ? activas.ordenes.length : '…'})
                </button>
              </div>

              {pestana === 'solicitudes' ? (
                <div key="solicitudes" className="conflictos-contenido">
                  {cargandoSolicitudes ? <Cargando /> : null}
                  {solicitudes && solicitudes.length === 0 ? <Vacio titulo="Sin solicitudes" detalle="Esta sede no tiene solicitudes." /> : null}
                  {solicitudes?.map((s) => (
                    <article className="sede-item sede-fila-solicitud sede-con-foto" key={s.id}>
                      <MiniaturaImagenTabla url={s.fotoUrl} nombre={`Solicitud #${s.numero}`} tamano={64} />
                      <div className="sede-item-contenido">
                        <div className="sede-item-cabecera">
                          <button type="button" className="sede-item-titulo" onClick={() => navegar(`/solicitudes/${s.id}`)}>
                            #{s.numero} {s.titulo}
                          </button>
                          <EstadoSolicitudEtiqueta estado={s.estado} />
                        </div>
                        {s.ubicacion ? <span className="sede-item-ubicacion">{s.ubicacion}</span> : null}
                        {s.ordenes.length > 0 ? (
                          <div className="sede-item-ots">
                            <span className="sede-item-ots-etiqueta">OT generadas</span>
                            {s.ordenes.map((o) => (
                              <button key={o.id} type="button" className="sede-ot-chip" onClick={() => navegar(`/ordenes/${o.id}`)}>
                                #{o.numero}
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div key="ots" className="conflictos-contenido">
                  {activas && activas.ordenes.length === 0 ? <Vacio titulo="Sin OT abiertas" detalle="Esta sede no tiene órdenes abiertas." /> : null}
                  {activas?.ordenes.map((o) => (
                    <article className={`sede-item sede-con-foto sede-fila-ot${o.vencida ? ' sede-fila-vencida' : ''}`} key={o.id}>
                      <MiniaturaImagenTabla url={o.fotoUrl} nombre={`OT #${o.numero}`} tamano={64} />
                      <div className="sede-item-contenido">
                        <div className="sede-item-cabecera">
                          <button type="button" className="sede-item-titulo" onClick={() => navegar(`/ordenes/${o.id}`)}>
                            #{o.numero} {o.titulo}
                          </button>
                          <span className={`sede-estado ${o.vencida ? 'sede-estado-falla' : 'sede-estado-ot'}`}>
                            {o.vencida ? 'Vencida' : ETIQUETA_ESTADO_OT[o.estado]}
                          </span>
                        </div>
                        {o.ubicacion ? <span className="sede-item-ubicacion">{o.ubicacion}</span> : null}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
}
